import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navBrands, navLabel, brandForPath, PIZZA_INN_LABEL } from '../../config/brands';
import useHideHeaderOnScroll from '../../hooks/useHideHeaderOnScroll';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaBars, 
  FaTimes, 
  FaShoppingCart, 
  FaPizzaSlice,
  FaChevronDown,
  FaUser,
  FaSignOutAlt
} from 'react-icons/fa';

import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { 
  getCurrentLanguage, 
  switchLanguage, 
  removeLanguageFromPath,
  storeLanguagePreference,
  LANGUAGE_NAMES,
  LANGUAGE_FLAGS 
} from '../../utils/languageUtils';
import MiniCart from '../ui/MiniCart';
import pizzaInnLogo from '../../assets/logo-pizza-inn.png';
import allLogo from '../../assets/all-logo.svg';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  const { restaurantInfo } = useApp();
  const { isAuthenticated, user, logout } = useAuth();
  const { totalItems } = useCart();
  
  // Get current language from URL
  const currentLang = getCurrentLanguage(location.pathname);
  const isAllMenu = location.pathname.includes('/menu/all-eat');

  // The venue the guest is in, if it is not Pizza Inn. The red button then
  // opens that venue's menu instead of Pizza Inn's.
  const pathNoLang = removeLanguageFromPath(location.pathname).replace(/(.)\/$/, '$1');
  const venue = brandForPath(pathNoLang);
  const ctaPath = venue ? venue.route : '/menu';
  // MokalandMenu renders on /mokaland and /mokaland/menu alike.
  const onVenueMenu = venue && (pathNoLang === venue.route
    || (venue.key === 'mokaland' && pathNoLang === '/mokaland/menu'));

  const ALL_BRAND = {
    bg:      '#49031d',
    cream:   '#f3e4c5',
    muted:   'rgba(243,228,197,0.55)',
    border:  'rgba(243,228,197,0.15)'
  };
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMiniCartOpen, setIsMiniCartOpen] = useState(false);

  // On a menu page the header slides away as the page is scrolled down and
  // returns on the way up, and the category bar pins in its place. The drawer
  // and the mini cart are fixed-position children of this element: it must not
  // move while either is open — nor while either is still animating out, or a
  // scroll in those ~300ms slides their containing block away mid-exit.
  const headerRef = useRef(null);
  const languageChipRef = useRef(null);
  const panelWasOpen = useRef(false);
  const [panelClosing, setPanelClosing] = useState(false);
  useEffect(() => {
    if (isMobileMenuOpen || isMiniCartOpen) {
      panelWasOpen.current = true;
      setPanelClosing(true);
      return undefined;
    }
    if (!panelWasOpen.current) return undefined;
    const timer = setTimeout(() => {
      panelWasOpen.current = false;
      setPanelClosing(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [isMobileMenuOpen, isMiniCartOpen]);
  const isHidden = useHideHeaderOnScroll(headerRef, {
    locked: isMobileMenuOpen || isMiniCartOpen || panelClosing || isLanguageDropdownOpen,
  });

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.language-switcher')) {
        setIsLanguageDropdownOpen(false);
      }
      if (!event.target.closest('.user-menu')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Close mobile menu, mini cart and the language list when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMiniCartOpen(false);
    setIsLanguageDropdownOpen(false);
  }, [location.pathname]);

  // The drawer and the language list are never open together.
  useEffect(() => {
    if (isMobileMenuOpen) setIsLanguageDropdownOpen(false);
  }, [isMobileMenuOpen]);

  // Manage body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-nav-open');
    } else {
      document.body.classList.remove('mobile-nav-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('mobile-nav-open');
    };
  }, [isMobileMenuOpen]);

  const languages = [
    { code: 'az', name: LANGUAGE_NAMES.az, flag: LANGUAGE_FLAGS.az },
    { code: 'en', name: LANGUAGE_NAMES.en, flag: LANGUAGE_FLAGS.en },
    { code: 'ru', name: LANGUAGE_NAMES.ru, flag: LANGUAGE_FLAGS.ru },
    { code: 'tr', name: LANGUAGE_NAMES.tr, flag: LANGUAGE_FLAGS.tr }
  ];

  // Garden, ALL, Sədəf and Moka Land, from the brand registry.
  const brandNavLinks = navBrands.map((b) => ({
    path: `/${currentLang}${b.route}`,
    label: navLabel(b, t),
  }));

  const navLinks = [
    { path: `/${currentLang}`, label: t('nav.home') },
    { path: `/${currentLang}/menu`, label: PIZZA_INN_LABEL },
    ...brandNavLinks,
    { path: `/${currentLang}/about`, label: t('nav.about') },
    // Gallery is out for now, at the owner's request.
    { path: `/${currentLang}/contact`, label: t('nav.contact') },
    { path: `/${currentLang}/location`, label: t('nav.location') },
    { path: `/${currentLang}/reservations`, label: t('nav.reservations') }
  ];

  // The four rooms, surfaced on small screens. Everything used to sit behind
  // the hamburger, so a guest on a phone could not see that this is a family of
  // venues at all -- Moka Land and the Garden were two taps away and invisible.
  // These scroll horizontally below the desktop breakpoint, in the same
  // hairline language as the menu's category strip.
  const venueLinks = [
    { path: `/${currentLang}/menu`, label: PIZZA_INN_LABEL },
    ...brandNavLinks
  ];

  // What the drawer lists: everything that is NOT already in the strip above.
  const drawerLinks = navLinks.filter(
    (link) => !venueLinks.some((v) => v.path === link.path)
  );

  const handleLanguageChange = (langCode) => {
    const newPath = switchLanguage(langCode, location.pathname);
    
    // Store the user's language preference
    storeLanguagePreference(langCode);
    
    navigate(newPath);
    i18n.changeLanguage(langCode);
    setIsLanguageDropdownOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate(`/${currentLang}`);
  };

  const handleCartClick = (e) => {
    e.preventDefault();
    if (totalItems > 0) {
      setIsMiniCartOpen(true);
    } else {
      navigate(`/${currentLang}/cart`);
    }
  };

  const handleMiniCartClose = () => {
    setIsMiniCartOpen(false);
  };

  const currentLanguage = languages.find(lang => lang.code === currentLang) || languages[0];

  return (
    <header
      ref={headerRef}
      className={`header${isScrolled ? ' scrolled' : ''}${isHidden ? ' header--hidden' : ''}`}
      style={isAllMenu ? { background: ALL_BRAND.bg, borderBottomColor: ALL_BRAND.border } : {}}
    >
      <div className="header-container">
        {/* Logo */}
        <Link to={`/${currentLang}${isAllMenu ? '/menu/all-eat' : ''}`} className="logo">
          {isAllMenu ? (
            <img src={allLogo} alt="ALL | Chef's Kitchen" style={{ height: 36, width: 'auto' }} />
          ) : (
            <>
              <div className="logo-icon">
                <img src={pizzaInnLogo} alt={restaurantInfo.name} className="logo-image" />
              </div>
              <span className="logo__wordmark">Pizza Inn</span>
              {/* Decorative. Dropped on narrow screens, where it was taking
                  the width the wordmark needed and eliding it to "Pizza I...". */}
              <span className="logo__est">est. 1995</span>
            </>
          )}
        </Link>

        {/* Desktop Navigation */}
        <nav className="nav">
          <ul className="nav-links">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                  style={isAllMenu ? { color: ALL_BRAND.cream } : {}}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop Actions */}
          <div className="d-flex align-center gap-md">
            {/* User Menu */}
            {isAuthenticated && (
              <div className="user-menu position-relative">
                <button
                  className="btn btn-secondary d-flex align-center gap-sm"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  style={isAllMenu ? { color: ALL_BRAND.cream, borderColor: ALL_BRAND.border, background: 'transparent' } : {}}
                >
                  <FaUser />
                  <span className="d-none d-md-inline">{user?.name || t('nav.profile')}</span>
                  <FaChevronDown className={`transition-transform ${isUserMenuOpen ? 'rotate-180' : 'rotate-0'}`} />
                </button>

                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      className="dropdown-menu"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      style={isAllMenu ? { background: '#3a0217', border: `1px solid ${ALL_BRAND.border}`, color: ALL_BRAND.cream } : {}}
                    >
                      <Link
                        to={`/${currentLang}/profile`}
                        className="dropdown-item text-decoration-none"
                        onClick={() => setIsUserMenuOpen(false)}
                        style={isAllMenu ? { color: ALL_BRAND.cream } : {}}
                      >
                        <FaUser />
                        {t('nav.profile')}
                      </Link>
                      <button
                        className="dropdown-item logout w-100 text-left border-0 bg-transparent"
                        onClick={handleLogout}
                        style={isAllMenu ? { color: ALL_BRAND.cream } : {}}
                      >
                        <FaSignOutAlt />
                        {t('nav.logout')}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Order Review Button — hidden on ALL menu (it's a QR read-only menu) */}
            {!isAllMenu && (
              <button
                className={`order-button ${totalItems > 0 ? 'has-items' : ''}`}
                onClick={handleCartClick}
                aria-label={t('nav.orderReview', 'Order Review')}
              >
                <FaShoppingCart />
                {totalItems > 0 && (
                  <span className="order-count animate-order-badge">{totalItems}</span>
                )}
                <span className="order-text d-none d-md-inline-block">
                  {totalItems > 0 ? t('nav.reviewOrder', 'Review Order') : t('nav.startOrder', 'Start Order')}
                </span>
              </button>
            )}

            {/* Quick Order Button for Desktop — the current venue's menu; hidden on ALL and where it would link to the page itself */}
            {!isAllMenu && !onVenueMenu && (
              <Link to={`/${currentLang}${ctaPath}`} className="btn btn-primary d-none d-lg-flex">
                <FaPizzaSlice className="me-2" />
                {t('nav.menu')}
              </Link>
            )}
          </div>
        </nav>

        {/* Language and the menu button, together at the right edge below the
            desktop breakpoint (where the hamburger shows). The switcher sits in
            the bar, one tap from anywhere, not in the drawer behind the
            hamburger: it is the one setting a first-time guest may need before
            they can read anything else. Desktop has its own controls. */}
        <div className="header-tools">
          <div
            className={`language-switcher${isAllMenu ? ' language-switcher--all' : ''}`}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && isLanguageDropdownOpen) {
                setIsLanguageDropdownOpen(false);
                languageChipRef.current?.focus();
              }
            }}
            // Tabbing past the last option closes the list. Only a real focus
            // target counts: Safari does not focus a clicked button, so a click
            // blurs to null, and outside clicks are the document handler's.
            onBlur={(e) => {
              if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
                setIsLanguageDropdownOpen(false);
              }
            }}
          >
            <button
              ref={languageChipRef}
              type="button"
              className="language-chip"
              onClick={() => setIsLanguageDropdownOpen((open) => !open)}
              aria-haspopup="true"
              aria-expanded={isLanguageDropdownOpen}
              aria-label={`${t('nav.language', 'Language')}: ${currentLanguage.name}`}
            >
              <span className="language-chip__flag" aria-hidden="true">{currentLanguage.flag}</span>
              <span className="language-chip__code">{currentLanguage.code.toUpperCase()}</span>
              <FaChevronDown
                className={`language-chip__chevron${isLanguageDropdownOpen ? ' open' : ''}`}
                aria-hidden="true"
              />
            </button>

            <AnimatePresence>
              {isLanguageDropdownOpen && (
                <motion.ul
                  className="language-list"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                >
                  {languages.map((lang) => {
                    const active = lang.code === currentLang;
                    return (
                      <li key={lang.code}>
                        <button
                          type="button"
                          aria-current={active ? 'true' : undefined}
                          className={`language-list__option${active ? ' active' : ''}`}
                          onClick={() => handleLanguageChange(lang.code)}
                        >
                          <span aria-hidden="true">{lang.flag}</span>
                          <span className="language-list__code">{lang.code.toUpperCase()}</span>
                          <span className="language-list__name">{lang.name}</span>
                        </button>
                      </li>
                    );
                  })}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          <button
            className="mobile-nav-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
            style={isAllMenu ? { color: ALL_BRAND.cream } : {}}
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Venue strip — hidden at the desktop breakpoint, where the same links
          already sit inline in the bar above. */}
      <nav className="venue-strip" aria-label={t('nav.venues', 'Our venues')}>
        <ul className="venue-strip__list">
          {venueLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`venue-strip__link ${location.pathname === link.path ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="mobile-nav open"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            style={isAllMenu ? { background: ALL_BRAND.bg, borderRightColor: ALL_BRAND.border } : {}}
          >
            <div className="mobile-nav-header" style={isAllMenu ? { borderBottomColor: ALL_BRAND.border } : {}}>
              <Link to={`/${currentLang}${isAllMenu ? '/menu/all-eat' : ''}`} className="logo" onClick={() => setIsMobileMenuOpen(false)}>
                {isAllMenu ? (
                  <img src={allLogo} alt="ALL | Chef's Kitchen" style={{ height: 32, width: 'auto' }} />
                ) : (
                  <>
                    <div className="logo-icon">
                      <img src={pizzaInnLogo} alt={restaurantInfo.name} className="logo-image" />
                    </div>
                    <span style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 500 }}>
                      Pizza Inn
                    </span>
                    <span style={{ fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', opacity: 0.5, display: 'block', lineHeight: 1, marginTop: '-2px' }}>
                      est. 1995
                    </span>
                  </>
                )}
              </Link>
              <button
                className="mobile-nav-close"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close mobile menu"
                style={isAllMenu ? { color: ALL_BRAND.cream } : {}}
              >
                <FaTimes />
              </button>
            </div>

            <ul className="mobile-nav-links">
              {/* The four venues are deliberately NOT repeated here. They sit
                  in the strip above the bar at all times, so listing them again
                  inside a drawer the customer has to open gave two routes to
                  the same pages and made the drawer look like the navigation
                  when it is only the long tail. */}
              {drawerLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`mobile-nav-link ${location.pathname === link.path ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    style={isAllMenu ? {
                      color: location.pathname === link.path ? ALL_BRAND.cream : ALL_BRAND.muted,
                      borderBottomColor: ALL_BRAND.border
                    } : {}}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link 
                  to={`/${currentLang}/order`}
                  className="mobile-nav-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('nav.order')}
                </Link>
              </li>
              {isAuthenticated && (
                <>
                  <li>
                    <Link 
                      to={`/${currentLang}/profile`}
                      className="mobile-nav-link"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('nav.profile')}
                    </Link>
                  </li>
                  <li>
                    <button 
                      className="mobile-nav-link w-100 text-left border-0 bg-transparent"
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      {t('nav.logout')}
                    </button>
                  </li>
                </>
              )}
            </ul>

            {/* The cart. On a phone the bar was carrying the logo, the
                wordmark, the language switcher, the cart and the menu button;
                the cart, the control used least, is here. The language
                switcher stays in the bar, beside the menu button. */}
            {!isAllMenu && (
              <button
                className="drawer-cart"
                onClick={() => { handleCartClick(); setIsMobileMenuOpen(false); }}
              >
                <FaShoppingCart aria-hidden="true" />
                <span>
                  {totalItems > 0
                    ? t('nav.reviewOrder', 'Review Order')
                    : t('nav.startOrder', 'Start Order')}
                </span>
                {totalItems > 0 && <span className="drawer-cart__count">{totalItems}</span>}
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu Backdrop */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            className="mobile-nav-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setIsMobileMenuOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'black',
              zIndex: 'var(--z-modal-backdrop)'
            }}
          />
        )}
      </AnimatePresence>

      {/* Mini Cart */}
      <MiniCart 
        isOpen={isMiniCartOpen}
        onClose={handleMiniCartClose}
      />
    </header>
  );
};

export default Header;
